// =============================================
// FIXED REPORTS VIEW COMPONENT
// components/ReportsView.js
// =============================================

import React, { useState } from 'react'
import { BarChart3, FileText, Users, Building2, FolderOpen, Calendar, Download, Filter, X, UserCheck } from 'lucide-react'
import { useReportsData } from '../hooks/useReportsData'
import { FiltersPanel } from './reports/FiltersPanel'
import { SummaryReport } from './reports/SummaryReport'
import { DetailedReport } from './reports/DetailedReport'
import { ClientReport } from './reports/ClientReport'
import { ProjectReport } from './reports/ProjectReport'
import { UserReport } from './reports/UserReport'
import { RoleReport } from './reports/RoleReport'
import { TimelineReport } from './reports/TimelineReport'
import { ExportControls } from './reports/ExportControls'

const ReportsView = ({ currentTenant }) => {
  const [activeTab, setActiveTab] = useState('summary')
  const { data, loading, error, filters, setFilters, refetch } = useReportsData(currentTenant)

  const reportTabs = [
    { id: 'summary', label: 'Summary', icon: BarChart3, component: SummaryReport },
    { id: 'detailed', label: 'Detailed', icon: FileText, component: DetailedReport },
    { id: 'clients', label: 'By Client', icon: Building2, component: ClientReport },
    { id: 'projects', label: 'By Project', icon: FolderOpen, component: ProjectReport },
    { id: 'users', label: 'By Team Member', icon: Users, component: UserReport },
    { id: 'roles', label: 'By Role', icon: UserCheck, component: RoleReport },
    { id: 'timeline', label: 'Timeline', icon: Calendar, component: TimelineReport }
  ]

  const ActiveReportComponent = reportTabs.find(tab => tab.id === activeTab)?.component

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-800 text-lg font-medium mb-2">Error Loading Reports</div>
          <div className="text-red-600 mb-4">{error}</div>
          <button 
            onClick={refetch}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports & Analytics</h1>
          <p className="text-slate-600 mt-1">Track performance and generate insights</p>
        </div>
        <ExportControls 
          activeTab={activeTab}
          data={data}
          filters={filters}
          currentTenant={currentTenant} // ← FIX: Added missing currentTenant prop
        />
      </div>

      {/* Filters */}
      <FiltersPanel 
        filters={filters} 
        onChange={setFilters}
        loading={loading}
      />

      {/* Report Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="border-b border-slate-200">
          <nav className="flex overflow-x-auto">
            {reportTabs.map(tab => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </nav>
        </div>

        {/* Report Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
                <div className="text-slate-600">Loading report data...</div>
              </div>
            </div>
          ) : (
            <ActiveReportComponent 
              data={data} 
              filters={filters}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// Export the component as default
export default ReportsView